import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { stripHtml } from '../../../utils/sanitize';

interface FailedChange {
  id: string;
  number: string;
  shortDescription: string;
  createdAt: string;
}

interface FailedChangeGroup {
  company: string;
  group: string;
  count: number;
  changes: FailedChange[];
}

interface FailedChangesTableProps {
  data: FailedChangeGroup[];
  onViewAll?: () => void;
}

const FailedChangesTable: React.FC<FailedChangesTableProps> = ({ data, onViewAll }) => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { formatDate } = useDateFormatter();

  if (data.length === 0) {
    return (
      <div id="failed-changes-table" className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Failed Change Requests</h3>
        <div className="h-32 flex items-center justify-center text-gray-500">
          No failed change requests found
        </div>
      </div>
    );
  }

  const toggleRow = (key: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  const totalFailed = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div id="failed-changes-table" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Failed Change Requests</h3>
          <p className="text-sm text-gray-500">Company and Assignment Group-wise breakdown</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-2xl font-bold text-red-600">{totalFailed}</span>
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              View All
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignment Group</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Failed Count</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item) => {
              const key = `${item.company}|${item.group}`;
              const isExpanded = expandedRows.has(key);

              return (
                <React.Fragment key={key}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRow(key)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {item.company}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {item.group}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {item.count}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded row with change details */}
                  {isExpanded && item.changes.length > 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-2 bg-gray-50">
                        <div className="ml-8 space-y-1">
                          {item.changes.map((change) => (
                            <div
                              key={change.id}
                              className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 hover:bg-gray-50 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/changes/${change.id}`);
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-primary-600">
                                  {change.number}
                                </span>
                                <span className="text-sm text-gray-600 truncate max-w-md">
                                  {stripHtml(change.shortDescription)}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(change.createdAt)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FailedChangesTable;
